import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { History } from './types'

/*
 * Bitrate over the last 5 minutes (the 5 s windows the live model already has), as the system's
 * ChartPalette / ScreenStatus charts draw it (AUDIT ST-01, ST-06): series in chart-1…4 in order —
 * never a state colour, so a line does not read as a state — on chart-grid rules, the value at the
 * right end next to the series' name. From the fifth series on the rest are context (chart-context).
 * A window without data breaks the line instead of drawing a zero. The numbers are also in the
 * table under it: the chart is aria-hidden apart from its label. It is drawn at the width it has
 * (one user unit = one CSS pixel), so its text keeps the system's 11 px at every width.
 */

export interface Series {
  name: string
  history: History
}

const PAD_L = 34
const PAD_T = 18
const PAD_B = 8
/** points in 5 minutes of 5 s windows */
const N = 60

const colour = (i: number) => (i < 4 ? `var(--chart-${i + 1})` : 'var(--chart-context)')

function niceTop(v: number): number {
  for (const s of [2, 4, 5, 8, 10, 12, 16, 20, 25, 30, 40, 50, 60, 80, 100]) if (s >= v) return s
  return Math.ceil(v / 50) * 50
}

/** Labels at the right end, pushed apart so two equal values stay readable. */
function spread(ys: number[], gap: number, lo: number, hi: number): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y)
  for (let k = 1; k < order.length; k++) order[k]!.y = Math.max(order[k]!.y, order[k - 1]!.y + gap)
  const over = (order[order.length - 1]?.y ?? 0) - hi
  if (over > 0) for (const o of order) o.y -= over
  for (let k = 0; k < order.length; k++) order[k]!.y = Math.max(order[k]!.y, lo + k * gap)
  const out: number[] = []
  for (const o of order) out[o.i] = o.y
  return out
}

export function BitrateChart({ series, label, height = 200, labelW = 120, testId }: { series: Series[]; label: string; height?: number; labelW?: number; testId?: string }) {
  const W = useWidth()
  const plotR = W.width - labelW
  const max = Math.max(1, ...series.flatMap((s) => s.history.map((p) => p.v)))
  const top = niceTop(max * 1.1)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(top * f * 10) / 10)
  const y = (v: number) => PAD_T + (height - PAD_T - PAD_B) * (1 - Math.min(v, top) / top)
  const x = (i: number, len: number) => PAD_L + ((i + Math.max(0, N - len)) / (N - 1)) * (plotR - PAD_L)
  const lines = series.map((s) => {
    const h = s.history.slice(-N)
    let d = ''
    let pen = false
    h.forEach((p, i) => {
      if (p.v < 0) return void (pen = false)
      d += `${pen ? 'L' : 'M'}${x(i, h.length).toFixed(1)} ${y(p.v).toFixed(1)}`
      pen = true
    })
    const last = h[h.length - 1]
    return { d, last: last && last.v >= 0 ? last.v : undefined, len: h.length }
  })
  const ends = spread(lines.map((l) => (l.last !== undefined ? y(l.last) : height - PAD_B)), 13, PAD_T, height - PAD_B)
  const empty = lines.every((l) => !l.d)
  return (
    <div ref={W.ref}>
    <svg viewBox={`0 0 ${W.width} ${height}`} width="100%" role="img" aria-label={label} data-testid={testId} className="block font-mono text-[11px]">
      {ticks.map((v) => (
        <g key={v}>
          <line x1={PAD_L} x2={plotR} y1={y(v)} y2={y(v)} style={{ stroke: 'var(--chart-grid)' }} />
          <text x={PAD_L - 8} y={y(v) + 4} textAnchor="end" className="fill-muted num">{v}</text>
        </g>
      ))}
      <text x={PAD_L} y={10} className="fill-muted">Mbps</text>
      {empty && <text x={(PAD_L + plotR) / 2} y={height / 2} textAnchor="middle" className="fill-muted">No data yet</text>}
      {lines.map((l, i) => {
        const c = { stroke: colour(i) } as CSSProperties
        return (
          <g key={i} data-series={series[i]!.name}>
            <path d={l.d} fill="none" strokeWidth={i < 4 ? 2 : 1.5} strokeLinejoin="round" style={c} />
            {l.last !== undefined && <circle cx={x(l.len - 1, l.len)} cy={y(l.last)} r="3" strokeWidth="2" style={{ fill: colour(i), stroke: 'var(--panel)' }} />}
            <text x={plotR + 8} y={ends[i]! + 4} className="fill-text-soft">
              {shortName(series[i]!.name, Math.floor((labelW - 40) / 6.6))} <tspan className="fill-text num">{l.last !== undefined ? l.last.toFixed(1) : '—'}</tspan>
            </text>
          </g>
        )
      })}
    </svg>
    </div>
  )
}

/** The width of the chart's box, followed with a ResizeObserver (640 until measured). */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e!.contentRect.width)
      if (w > 0) setWidth(Math.max(320, w))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, width }
}

const shortName = (s: string, max: number) => (s.length > max ? s.slice(0, Math.max(1, max - 1)) + '…' : s)
